import { useMutation, useQueryClient } from "@tanstack/react-query";
import { assetUrl, writeJson } from "@libertify/experience-viewer";
import { API_URL } from "@/constants";
import { useAuth } from "hooks/useAuth";
import { standoutsKey } from "./helpers";
import type {
  AssetPatchBody,
  SaveStandoutsVariables,
  StandoutsContent,
  StandoutsSaver,
} from "./types";

/** Description fallback for a save with no read cached: the generator names
 *  the asset after its add-on type. */
const FALLBACK_DESCRIPTION = "Standouts";

/**
 * Writes a project's Standouts back to its asset:
 * `PATCH /media/assets/{assetId}` with `{ add_on: { ...document, standouts } }`.
 *
 * Caches like `useSaveQuiz`: written into the query cache when the save
 * STARTS and not invalidated, so a card reopened mid-flight seeds from the
 * edit. On failure the cache goes back to what it held before.
 */
export const useSaveStandouts = (projectId: string): StandoutsSaver => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = standoutsKey(projectId);

  const mutation = useMutation({
    mutationFn: (next: SaveStandoutsVariables) =>
      writeJson(
        `${assetUrl(API_URL, next.assetId)}?description=${encodeURIComponent(next.description)}`,
        { add_on: next.document } satisfies AssetPatchBody,
        accessToken
      ),
    onMutate: async (next) => {
      // A read still in flight would land after this write and put the
      // pre-edit list back.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<StandoutsContent>(queryKey);
      // Merged, so the project documents read alongside survive the save.
      queryClient.setQueryData<StandoutsContent>(queryKey, { ...previous, ...next });
      return { previous };
    },
    onError: (_error, _next, context) => {
      // setQueryData ignores `undefined`, so an empty cache is emptied again.
      if (context?.previous) {
        queryClient.setQueryData<StandoutsContent>(queryKey, context.previous);
      } else {
        queryClient.removeQueries({ queryKey });
      }
    },
  });

  return {
    save: (assetId, standouts) => {
      const cached = queryClient.getQueryData<StandoutsContent>(queryKey);
      const sameAsset = cached?.assetId === assetId;
      return mutation.mutateAsync({
        assetId,
        description:
          sameAsset && cached.description
            ? cached.description
            : FALLBACK_DESCRIPTION,
        document: { ...(sameAsset ? cached.document : null), standouts },
      });
    },
    isSaving: mutation.isLoading,
  };
};
